import React, { useState, useEffect } from 'react';
import { Bot, MessageSquare, RefreshCw, Eye, Star, ShoppingCart, Globe, Camera, UserPlus, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from './services/api';
import { Panel, Section, Badge, LoadingSpinner } from './components';

function OliviaPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('followups');
  const [expandedPosts, setExpandedPosts] = useState({});
  const [oliviaSettings, setOliviaSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchOliviaData();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const result = await apiService.getOliviaSettings();
      setOliviaSettings(result);
    } catch (err) {
      console.error('Error fetching Olivia settings:', err);
    }
  };

  const updateSetting = async (key, value) => {
    const updated = { ...oliviaSettings, [key]: String(value) };
    setOliviaSettings(updated);
    setSavingSettings(true);
    try {
      await apiService.updateOliviaSettings({ [key]: String(value) });
    } catch (err) {
      console.error('Error saving setting:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchOliviaData = async () => {
    try {
      setLoading(true);
      const result = await apiService.get('/amn/olivia/activity/');
      setData(result);
      setMessages(result.messages || []);
      setStats(result.stats || null);
    } catch (err) {
      console.error('Error fetching Olivia data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (isToday) return `Today ${time}`;
    if (isYesterday) return `Yesterday ${time}`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + time;
  };

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Header */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <Bot className="w-4 h-4 text-purple-700" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 leading-none">Olivia</h1>
              <p className="text-xs text-gray-500">AI Follow-Up & Review Management</p>
            </div>
          </div>
          <button onClick={fetchOliviaData} className="p-1.5 rounded hover:bg-gray-100">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {loading ? (
          <Panel className="p-6"><LoadingSpinner /></Panel>
        ) : (
          <>
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                <Panel className="!p-2 text-center">
                  <div className="text-lg font-bold text-gray-900">{stats.total_sent}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">Texts Sent</div>
                </Panel>
                <Panel className="!p-2 text-center">
                  <div className="text-lg font-bold text-green-600">{stats.replied}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">Replied</div>
                </Panel>
                <Panel className="!p-2 text-center">
                  <div className="text-lg font-bold text-purple-600">{stats.reply_rate}%</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">Reply Rate</div>
                </Panel>
                <Panel className="!p-2 text-center">
                  <div className="text-lg font-bold text-yellow-600">{stats.review_replies || 0}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">Reviews Replied</div>
                </Panel>
                <Panel className="!p-2 text-center">
                  <div className="text-lg font-bold text-yellow-500">{stats.unreplied_5star || 0}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">⭐ Pending</div>
                </Panel>
                <Panel className="!p-2 text-center">
                  <div className="text-lg font-bold text-blue-600">{stats.gbp_posts || 0}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">GBP Posts</div>
                </Panel>
                <Panel className="!p-2 text-center">
                  <div className="text-lg font-bold text-teal-600">{stats.gbp_photos || 0}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">📷 Photos</div>
                </Panel>
                <Panel className="!p-2 text-center">
                  <div className="text-lg font-bold text-rose-600">{stats.lead_followups || 0}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">Lead Outreach</div>
                </Panel>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 mb-3 bg-white rounded-lg p-1 shadow-sm">
              <button
                onClick={() => setActiveTab('followups')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'followups' ? 'bg-purple-700 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                Follow-ups ({messages.length})
              </button>
              <button
                onClick={() => setActiveTab('leads')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'leads' ? 'bg-rose-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <UserPlus className="w-3.5 h-3.5" />
                Leads ({data?.lead_followups?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'reviews' ? 'bg-yellow-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Star className="w-3.5 h-3.5" />
                Reviews ({data?.review_replies?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('abandoned')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'abandoned' ? 'bg-orange-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <ShoppingCart className="w-3.5 h-3.5" />
                Recovery ({data?.abandoned_recoveries?.filter(a => !a.booked_elsewhere && !a.recovery_sent).length || 0})
              </button>
              <button
                onClick={() => setActiveTab('gbp')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'gbp' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Globe className="w-3.5 h-3.5" />
                Posts ({data?.gbp_posts?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('photos')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'photos' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Camera className="w-3.5 h-3.5" />
                Photos ({data?.gbp_photos?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'settings' ? 'bg-gray-700 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Settings className="w-3.5 h-3.5" />
                Settings
              </button>
            </div>

            {/* Follow-ups Tab */}
            {activeTab === 'followups' && (
              <Panel>
                <Section title={`Follow-up Messages (${messages.length})`}>
                  {messages.length === 0 ? (
                    <p className="text-xs text-gray-500 py-4 text-center">No messages sent yet.</p>
                  ) : (
                    <div className="space-y-1">
                      {messages.map((msg, idx) => (
                        <div key={idx} className={`rounded-lg border px-3 py-2 ${
                          msg.has_reply ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                        }`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{msg.client_name}</span>
                              {msg.type === 'rebook' && (
                                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-medium rounded">Rebook</span>
                              )}
                              {msg.has_reply ? (
                                <Badge variant="success" small>Replied</Badge>
                              ) : (
                                <Badge variant="neutral" small>No reply</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-gray-400">{formatDate(msg.sent_at)}</span>
                              {msg.client_id && (
                                <button
                                  onClick={() => navigate(`/clients/${msg.client_id}`)}
                                  className="text-gray-400 hover:text-purple-600"
                                  title="View client"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">{msg.message}</p>
                          {msg.reply && (
                            <div className="mt-1.5 pl-3 border-l-2 border-green-300">
                              <p className="text-xs text-green-800 leading-relaxed">{msg.reply}</p>
                              <p className="text-[10px] text-green-500 mt-0.5">{formatDate(msg.reply_at)}</p>
                            </div>
                          )}
                          {msg.priority_score && (
                            <p className="text-[10px] text-gray-400 mt-1">
                              Score: {msg.priority_score.toFixed(1)} · {msg.days_since} days since last appt
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              </Panel>
            )}

            {/* Lead Follow-ups Tab */}
            {activeTab === 'leads' && (
              <Panel>
                <Section title={`Lead Follow-ups (${data?.lead_followups?.length || 0})${stats?.lead_replied ? ` · ${stats.lead_replied} replied` : ''}`}>
                  {!data?.lead_followups?.length ? (
                    <p className="text-xs text-gray-500 py-4 text-center">No lead follow-ups sent yet. Olivia will start reaching out to unconverted leads at 11:30 AM on weekdays.</p>
                  ) : (
                    <div className="space-y-1">
                      {data.lead_followups.map((lf, idx) => (
                        <div key={idx} className={`rounded-lg border px-3 py-2 ${
                          lf.has_reply ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                        }`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => navigate(`/leads/${lf.person_id}`)}
                                className="text-sm font-medium text-gray-900 hover:text-rose-700 transition-colors"
                                title="View lead details"
                              >
                                {lf.name}
                              </button>
                              {lf.phone && (
                                <span className="text-[10px] text-gray-400">{lf.phone}</span>
                              )}
                              {lf.inquiry_topic && (
                                <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-medium rounded">{lf.inquiry_topic}</span>
                              )}
                              {lf.has_reply ? (
                                <Badge variant="success" small>Replied</Badge>
                              ) : (
                                <Badge variant="neutral" small>No reply</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-gray-400">{formatDate(lf.sent_at)}</span>
                              <button
                                onClick={() => navigate(`/leads/${lf.person_id}`)}
                                className="text-gray-400 hover:text-rose-600"
                                title="View lead"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">{lf.message}</p>
                          {lf.reply && (
                            <div className="mt-1.5 pl-3 border-l-2 border-green-300">
                              <p className="text-xs text-green-800 leading-relaxed">{lf.reply}</p>
                            </div>
                          )}
                          {lf.days_since_inquiry && (
                            <p className="text-[10px] text-gray-400 mt-1">
                              {lf.days_since_inquiry} days after inquiry · Priority: {lf.priority_score}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              </Panel>
            )}

            {/* Google Reviews Tab */}
            {activeTab === 'reviews' && (
              <Panel>
                <Section title={`Google Review Replies (${data?.review_replies?.length || 0})${stats?.unreplied_5star ? ` · ${stats.unreplied_5star} pending` : ''}`}>
                  {!data?.review_replies?.length ? (
                    <p className="text-xs text-gray-500 py-4 text-center">No review replies yet.</p>
                  ) : (
                    <div className="space-y-1">
                      {data.review_replies.map((r, idx) => (
                        <div key={idx} className="rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <div className="flex">
                                {[...Array(r.rating)].map((_, i) => (
                                  <Star key={i} className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                ))}
                              </div>
                              <span className="text-sm font-medium text-gray-900">{r.reviewer_name}</span>
                              {r.location && <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{r.location}</span>}
                            </div>
                            <span className="text-[10px] text-gray-400">{formatDate(r.reply_at)}</span>
                          </div>
                          {r.comment && (
                            <p className="text-xs text-gray-500 italic mb-1.5">"{r.comment.slice(0, 200)}{r.comment.length > 200 ? '...' : ''}"</p>
                          )}
                          <div className="pl-3 border-l-2 border-yellow-300">
                            <p className="text-xs text-gray-700 leading-relaxed">{r.reply}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              </Panel>
            )}

            {/* Abandoned Recovery Tab */}
            {activeTab === 'abandoned' && (
              <Panel>
                <Section title="Abandoned Booking Recovery">
                  {!data?.abandoned_recoveries?.length ? (
                    <p className="text-xs text-gray-500 py-4 text-center">No abandoned bookings found.</p>
                  ) : (
                    <div className="space-y-1">
                      {data.abandoned_recoveries.map((a, idx) => (
                        <div key={idx} className={`rounded-lg border px-3 py-2 ${
                          a.booked_elsewhere ? 'bg-green-50 border-green-200 opacity-60'
                          : a.recovery_sent ? 'bg-orange-50 border-orange-200'
                          : 'bg-white border-gray-200'
                        }`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{a.name}</span>
                              {a.booked_elsewhere ? (
                                <Badge variant="success" small>Booked elsewhere</Badge>
                              ) : a.recovery_sent ? (
                                <Badge variant="warning" small>Recovery sent</Badge>
                              ) : (
                                <Badge variant="neutral" small>Pending</Badge>
                              )}
                              <span className="text-[10px] text-gray-400">${a.amount}</span>
                            </div>
                            <span className="text-[10px] text-gray-400">{a.days_ago}d ago</span>
                          </div>
                          <p className="text-xs text-gray-500">{a.phone} · {a.email}</p>
                          {a.recovery_message && (
                            <div className="mt-1.5 pl-3 border-l-2 border-orange-300">
                              <p className="text-xs text-orange-800 leading-relaxed">{a.recovery_message}</p>
                              <p className="text-[10px] text-orange-500 mt-0.5">{formatDate(a.recovery_date)}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              </Panel>
            )}

            {/* GBP Posts Tab */}
            {activeTab === 'gbp' && (
              <Panel>
                <Section title={`Google Business Profile Posts (${data?.gbp_posts?.length || 0})`}>
                  {!data?.gbp_posts?.length ? (
                    <p className="text-xs text-gray-500 py-4 text-center">No GBP posts published yet.</p>
                  ) : (
                    <div className="space-y-1">
                      {data.gbp_posts.map((post, idx) => {
                        const isExpanded = expandedPosts[idx];
                        const isLong = post.content && post.content.length > 150;
                        return (
                        <div key={idx} className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="info" small>{post.location}</Badge>
                              {post.has_photo && <span className="text-[10px] text-gray-400">📷 with photo</span>}
                            </div>
                            <span className="text-[10px] text-gray-400">{formatDate(post.posted_at)}</span>
                          </div>
                          <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-line">
                            {isExpanded || !isLong ? post.content : post.content.slice(0, 150) + '...'}
                          </p>
                          {isLong && (
                            <button
                              onClick={() => setExpandedPosts(prev => ({ ...prev, [idx]: !prev[idx] }))}
                              className="text-[10px] text-blue-600 hover:text-blue-800 font-medium mt-1"
                            >
                              {isExpanded ? 'Show less' : 'Show more'}
                            </button>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </Section>
              </Panel>
            )}

            {/* Photos Tab */}
            {activeTab === 'photos' && (
              <Panel>
                <Section title={`GBP Photo Uploads (${data?.gbp_photos?.length || 0})`}>
                  {!data?.gbp_photos?.length ? (
                    <p className="text-xs text-gray-500 py-4 text-center">No photos uploaded yet. Scheduled every Wednesday at 9 AM.</p>
                  ) : (
                    <>
                      {/* Per-location summary badges */}
                      {stats?.gbp_photos_by_location && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {Object.entries(stats.gbp_photos_by_location)
                            .sort((a, b) => b[1] - a[1])
                            .map(([loc, count]) => (
                              <span key={loc} className="px-2 py-0.5 text-[10px] bg-teal-50 text-teal-700 border border-teal-200 rounded-full font-medium">
                                {loc}: {count}
                              </span>
                            ))}
                        </div>
                      )}

                      {/* Photo grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {data.gbp_photos.map((photo, idx) => (
                          <div key={idx} className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50 group">
                            {photo.image_url ? (
                              <img
                                src={photo.image_url}
                                alt={`${photo.location} upload`}
                                className="w-full aspect-[4/3] object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full aspect-[4/3] flex items-center justify-center bg-gray-100">
                                <Camera className="w-6 h-6 text-gray-300" />
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                              <p className="text-[10px] text-white font-medium">{photo.location}</p>
                              <p className="text-[9px] text-white/70">
                                {formatDate(photo.uploaded_at)}
                                {photo.original_kb && photo.enhanced_kb && (
                                  <> · {photo.original_kb}→{photo.enhanced_kb}KB</>
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </Section>
              </Panel>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && oliviaSettings && (
              <Panel>
                <Section title="Olivia Settings">
                  <p className="text-xs text-gray-500 mb-4">Control how often and how many messages Olivia sends. Changes take effect on the next scheduled run.</p>
                  <div className="space-y-4">
                    {[
                      { group: 'Client Follow-ups', desc: 'Re-engage past clients who haven\'t rebooked (weekdays 10 AM)', fields: [
                        { key: 'followup_enabled', label: 'Enabled', type: 'toggle' },
                        { key: 'followup_max_clients', label: 'Max per run', type: 'number' },
                        { key: 'followup_cooldown_days', label: 'Min gap (days)', type: 'number', hint: 'Min days between messages to same person' },
                      ]},
                      { group: 'Rebook Nudge', desc: 'One-time thank you + soft rebook the day after an appointment (Mon-Sat 10:15 AM)', fields: [
                        { key: 'rebook_enabled', label: 'Enabled', type: 'toggle' },
                        { key: 'rebook_max_clients', label: 'Max per run', type: 'number' },
                        { key: 'rebook_cooldown_days', label: 'Min gap (days)', type: 'number', hint: 'Skip if any Olivia message was sent within this many days' },
                      ]},
                      { group: 'Abandoned Booking', desc: 'One-time follow up on incomplete online bookings (weekdays, every 2h)', fields: [
                        { key: 'abandoned_enabled', label: 'Enabled', type: 'toggle' },
                        { key: 'abandoned_max_contacts', label: 'Max per run', type: 'number' },
                        { key: 'abandoned_cooldown_days', label: 'Min gap (days)', type: 'number', hint: 'Skip if any Olivia message was sent within this many days' },
                      ]},
                      { group: 'Lead Follow-up', desc: 'Reach out to unconverted prospects (weekdays 11:30 AM)', fields: [
                        { key: 'lead_enabled', label: 'Enabled', type: 'toggle' },
                        { key: 'lead_max_leads', label: 'Max per run', type: 'number' },
                        { key: 'lead_cooldown_days', label: 'Min gap (days)', type: 'number', hint: 'Min days between messages to same person' },
                      ]},
                      { group: 'Google Review Replies', desc: 'Auto-reply to 5-star Google reviews (weekdays 11 AM)', fields: [
                        { key: 'review_enabled', label: 'Enabled', type: 'toggle' },
                        { key: 'review_max_reviews', label: 'Max per run', type: 'number' },
                      ]},
                    ].map(section => (
                      <div key={section.group} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="text-sm font-semibold text-gray-900">{section.group}</h3>
                          {savingSettings && <span className="text-[10px] text-gray-400">Saving...</span>}
                        </div>
                        <p className="text-[10px] text-gray-500 mb-3">{section.desc}</p>
                        <div className="grid grid-cols-3 gap-3">
                          {section.fields.map(f => (
                            <div key={f.key}>
                              <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1" title={f.hint || ''}>{f.label}</label>
                              {f.type === 'toggle' ? (
                                <button
                                  onClick={() => updateSetting(f.key, oliviaSettings[f.key] === 'true' ? 'false' : 'true')}
                                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                                    oliviaSettings[f.key] === 'true'
                                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                                  }`}
                                >
                                  {oliviaSettings[f.key] === 'true' ? 'On' : 'Off'}
                                </button>
                              ) : (
                                <input
                                  type="number"
                                  min="1"
                                  max="30"
                                  value={oliviaSettings[f.key] || ''}
                                  onChange={e => updateSetting(f.key, e.target.value)}
                                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              </Panel>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default OliviaPage;
